import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  approveDraft,
  dismissDraft,
  fetchLeadPendingDrafts,
  createFollowUpTask,
} from "../../api/communications";
import { setLeadOutcome } from "../../api/leadsApi";
import "./LeadActionBar.css";

/* Lead-detail action bar. TWO surfaces, never blurred:

   1. OUTREACH APPROVAL — the only thing here that sends. Reuses the exact
      "Needs reply" path (fetchLeadPendingDrafts / approveDraft / dismissDraft);
      per-reply only, and the send re-checks suppression server-side.
   2. OUTCOME / STATUS — data only, sends nothing. Stage, an outcome note, a
      "do not contact" clean exit (writes the suppression ledger), and a
      follow-up that creates a task. No send button lives in this block.
*/

// UI stage -> alias the backend normalizes (leads_app/pipeline.py).
const STAGES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "quoted", label: "Quoted" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

// Backend pipeline_stage -> the UI stage it lights up.
const STAGE_FROM_BACKEND = {
  NEW: "new",
  CONTACTED: "contacted",
  QUALIFIED: "qualified",
  ESTIMATE_SCHEDULED: "quoted",
  ESTIMATE_SENT: "quoted",
  FOLLOW_UP: "contacted",
  BOOKED: "won",
  LOST: "lost",
};

export default function LeadActionBar({ leadId, record, onUpdated }) {
  const [drafts, setDrafts] = useState([]);
  const [draftBodies, setDraftBodies] = useState({});
  const [busyDraft, setBusyDraft] = useState(null);

  const [note, setNote] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [working, setWorking] = useState(false);

  const currentStage =
    STAGE_FROM_BACKEND[
      String(record?.pipeline_stage || record?.raw?.pipeline_stage || "").toUpperCase()
    ] || null;

  const loadDrafts = useCallback(async () => {
    if (!leadId) return;
    try {
      const data = await fetchLeadPendingDrafts(leadId);
      const rows = data?.results || [];
      setDrafts(rows);
      setDraftBodies(Object.fromEntries(rows.map((d) => [d.id, d.body || ""])));
    } catch {
      /* a card that can't load drafts just shows no outreach block */
    }
  }, [leadId]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  /* ── OUTREACH APPROVAL (sends) ─────────────────────────────── */
  const handleSend = async (draft) => {
    const body = (draftBodies[draft.id] || "").trim();
    if (!body) {
      toast.error("Message body is required.");
      return;
    }
    setBusyDraft(draft.id);
    try {
      await approveDraft(draft.id, body);
      toast.success("Reply sent.");
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
    } catch (err) {
      const status = err?.response?.status;
      const reason = err?.response?.data?.reason || err?.response?.data?.detail;
      if (status === 409) {
        toast.error(`Not sent — ${reason || "contact is suppressed"}.`);
        setDrafts((prev) => prev.filter((d) => d.id !== draft.id)); // it's gone from the queue now
      } else if (status === 502) {
        toast.error(`Not sent — ${reason || "the message did not dispatch"}.`);
      } else {
        toast.error("Could not send the reply.");
      }
    } finally {
      setBusyDraft(null);
    }
  };

  const handleDiscard = async (draft) => {
    setBusyDraft(draft.id);
    try {
      await dismissDraft(draft.id);
      toast.info("Draft discarded — you'll answer by hand.");
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
    } catch {
      toast.error("Could not discard the draft.");
    } finally {
      setBusyDraft(null);
    }
  };

  /* ── OUTCOME / STATUS (sends nothing) ──────────────────────── */
  const applyStage = async (stageKey) => {
    setWorking(true);
    try {
      await setLeadOutcome(leadId, {
        stage: stageKey,
        ...(note.trim() ? { outcome_note: note.trim() } : {}),
      });
      toast.success(`Marked ${stageKey}.`);
      setNote("");
      onUpdated?.();
    } catch {
      toast.error("Could not update status.");
    } finally {
      setWorking(false);
    }
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    setWorking(true);
    try {
      await setLeadOutcome(leadId, { outcome_note: note.trim() });
      toast.success("Outcome note saved.");
      setNote("");
      onUpdated?.();
    } catch {
      toast.error("Could not save the note.");
    } finally {
      setWorking(false);
    }
  };

  const markDoNotContact = async () => {
    const name = record?.first_name || record?.full_name || "this contact";
    if (
      !window.confirm(
        `Mark ${name} as "do not contact"? They will be removed from every future send.`
      )
    ) {
      return;
    }
    setWorking(true);
    try {
      await setLeadOutcome(leadId, {
        do_not_contact: true,
        ...(note.trim() ? { outcome_note: note.trim() } : {}),
      });
      toast.success("Marked do-not-contact — removed from future sends.");
      setNote("");
      onUpdated?.();
    } catch {
      toast.error("Could not mark do-not-contact.");
    } finally {
      setWorking(false);
    }
  };

  const createFollowUp = async () => {
    if (!followUpDate) return;
    setWorking(true);
    try {
      const name =
        record?.full_name ||
        [record?.first_name, record?.last_name].filter(Boolean).join(" ") ||
        "lead";
      await createFollowUpTask({
        lead: leadId,
        title: `Follow up: ${name}`,
        description: note.trim() || "",
        task_type: "follow_up",
        due_date: new Date(`${followUpDate}T09:00:00`).toISOString(),
      });
      toast.success(`Follow-up task created for ${followUpDate}.`);
      setFollowUpDate("");
      onUpdated?.();
    } catch {
      toast.error("Could not create the follow-up task.");
    } finally {
      setWorking(false);
    }
  };

  if (!leadId) return null;

  return (
    <div className="lab">
      {/* 1. OUTREACH APPROVAL — the only block that sends */}
      {drafts.length > 0 && (
        <section className="lab-outreach" aria-label="Outreach approval">
          <div className="lab-head">
            <span className="lab-dot lab-dot--send" />
            <h4>Outreach — needs your approval</h4>
            <span className="lab-sub">Sending is the only action here.</span>
          </div>
          {drafts.map((draft) => (
            <div key={draft.id} className="lab-draft">
              {draft.inbound?.body ? (
                <div className="lab-inbound">
                  <span className="lab-inbound-tag">They said</span>
                  <span className="lab-inbound-body">{draft.inbound.body}</span>
                </div>
              ) : null}
              {draft.campaign_goal ? (
                <div className="lab-goal">Goal: {draft.campaign_goal}</div>
              ) : null}
              <textarea
                className="lab-draft-body"
                value={draftBodies[draft.id] ?? ""}
                onChange={(e) =>
                  setDraftBodies((prev) => ({ ...prev, [draft.id]: e.target.value }))
                }
                rows={3}
                disabled={busyDraft === draft.id}
              />
              <div className="lab-draft-actions">
                <button
                  type="button"
                  className="lab-btn lab-btn--send"
                  onClick={() => handleSend(draft)}
                  disabled={busyDraft === draft.id}
                >
                  Send
                </button>
                <button
                  type="button"
                  className="lab-btn lab-btn--ghost"
                  onClick={() => handleDiscard(draft)}
                  disabled={busyDraft === draft.id}
                >
                  Discard
                </button>
                <span className="lab-edit-hint">Edit above before sending</span>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* 2. OUTCOME / STATUS — data only, never sends */}
      <section className="lab-outcome" aria-label="Outcome and status">
        <div className="lab-head">
          <span className="lab-dot lab-dot--data" />
          <h4>Outcome &amp; status</h4>
          <span className="lab-sub">Updates the record — sends nothing.</span>
        </div>

        <div className="lab-stages">
          {STAGES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`lab-stage${currentStage === s.key ? " is-active" : ""}${
                s.key === "lost" ? " lab-stage--lost" : ""
              }`}
              onClick={() => applyStage(s.key)}
              disabled={working}
            >
              {s.label}
            </button>
          ))}
        </div>

        <textarea
          className="lab-note"
          placeholder="Outcome note (optional) — applies to the status change, the note, or the exit below"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />

        <div className="lab-outcome-actions">
          <button
            type="button"
            className="lab-btn lab-btn--ghost"
            onClick={saveNote}
            disabled={working || !note.trim()}
          >
            Save note
          </button>

          <div className="lab-followup">
            <input
              type="date"
              className="lab-date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              aria-label="Follow-up date"
            />
            <button
              type="button"
              className="lab-btn lab-btn--ghost"
              onClick={createFollowUp}
              disabled={working || !followUpDate}
            >
              Follow up (task)
            </button>
          </div>

          <button
            type="button"
            className="lab-btn lab-btn--danger"
            onClick={markDoNotContact}
            disabled={working}
            title="Sets do-not-contact and writes the suppression ledger"
          >
            Not interested / Do&nbsp;not&nbsp;contact
          </button>
        </div>
      </section>
    </div>
  );
}
